import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SHARED_COMPONENTS } from '../../../../shared/shared-imports';
import { customValidator } from '../../../../Utils/validator';
import { AccountManagementSharedService } from '../services/account-management-shared.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { LookupKey } from '../../../../shared/constants/lookup-keys';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { ApiService } from '../../../../core/services/api.service';
import { Utils } from '../../../../Utils/utils';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { FieldValue } from '../../../../shared/constants/services.enum';
import { OptionTranslateService } from '../../../../shared/services/option-translate.service';
import { Location } from '@angular/common';
import { DcMethodValues } from '../../../../shared/constants/dc-method.constants';

@Component({
  selector: 'app-add-update-user',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './add-update-user.component.html',
  styleUrl: './add-update-user.component.scss'
})
export class AddUpdateUserComponent implements OnInit {
  form: FormGroup;
  type: 'add' | 'update' = 'add';
  req: any = {};
  csn: string = '';
  userId: string = '';
  loader: boolean = false;
  submitLoader: boolean = false;
  lang: string = 'en';
  standardOption: any = [];
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private accountManagementSharedService: AccountManagementSharedService,
    private sharedService: SharedService,
    private api: ApiService,
    private router: Router,
    private optionTranslateService: OptionTranslateService,
    private location: Location
  ) {
    this.route.data.subscribe(data => {
      this.type = data['type'];
    });
    this.standardOption = this.optionTranslateService.getOptions(
      'standardOption',
      this.lang
    );
    this.getUserTypes();

    this.form = this.fb.group({
      enName: ['', [Validators.required, customValidator('english')]],
      arName: ['', [Validators.required]],
      nationality: ['', [Validators.required]],
      userType: ['', [Validators.required]],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      phone: [{ value: '', disabled: true }, Validators.required],
      access: [{ value: [], disabled: true }],
      notif: [[]],
      viewDocument: ['', [Validators.required]],
      admin: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.lang = this.sharedService.getCurrentLang();
    this.route.params.subscribe(params => {
      this.csn = params['csn'] || '';
      this.userId = params['userId'] || '';

      if (this.type === 'update' && this.csn && this.userId) {
        this.loadUserDetails();
      }
    });
  }
  userTypes: any[] = [];
  getUserTypes() {
    let request = {
      body: {
        ProcessName: "DC Get List Of Values WF",
        Type: LookupKey.DC_USER_TYPE,
        "LanguageCode": this.sharedService.getCurrentLang() == "ar" ? 'ARA' : "ENU",
        ParentValue: ''
      }
    }
    this.api
      .post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      .subscribe({
        next: (result: any) => {
          if (result.SiebelMessage?.['List Of Values'] && result["Error Message"] == null || result["Error Message"] == "") {
            this.userTypes = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
          }
        }
      })
  }

  get g(): any { return this.form.controls; }
  get ut(): any { return (this.form.get('userType') as any).controls; }
  get nf(): any { return (this.form.get('notif') as any).controls; }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.csn || !this.userId) {
      this.sharedService.toastMessage('ERROR', 'Missing user information', 'error');
      return;
    }

    const notifSelection: string[] = this.form.get('notif')?.value || [];
    const notifPayload = this.mapNotifSelectionToPayload(notifSelection);
    const accessSelection: string[] = this.form.get('access')?.value || [];
    const accessPayload = this.mapAccessSelectionToPayload(accessSelection);

    const viewDocumentValue = this.form.get('viewDocument')?.value;
    const viewDocumentFlag = this.isYesValue(viewDocumentValue) ? 'Y' : 'N';

    const adminValue = this.form.get('admin')?.value;
    const adminVal = this.isYesValue(adminValue) ? 'Y' : 'N';

    let requestPayload: any = {
      body: {
        ProcessName: "DC Update User Notification Preferences WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.ADD_UPDATE_USER,
        "Login Name": this.userId,
        CSN: this.csn,
        "All Services": notifPayload.allServices,
        "COO Services Only": notifPayload.cooServicesOnly,
        "SVS Services Only": notifPayload.svsServicesOnly,
        Transactional: notifPayload.transactional,
        "User Type": this.form.get('userType')?.value || '',
        "DC View Document Flag": viewDocumentFlag,
        "DC Admin Flag": adminVal,
        ...accessPayload
      }
    }

    this.submitLoader = true;
    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.submitLoader = false;
          if (!response['Error Code'] && !response['Error Message']) {
            this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
            this.location.back();
          } else {
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
          }
        },
        error: () => {
          this.submitLoader = false;
        }
      });
  }
  private loadUserDetails(): void {
    this.loader = true;
    this.accountManagementSharedService
      .getUserAccessDetails(this.csn, this.userId, DcMethodValues.ADD_UPDATE_USER, this.lang)
      .subscribe({
        next: (response: any) => {
          this.loader = false;
          if (response['Error Code'] == "" && response['Error Message'] == "") {
            const detail = response?.SiebelMessage?.["User Details"];
            //const accessDetail = (typeof(detail?.['User Access Details']) == 'object') ?detail?.['User Access Details'][0] : detail?.['User Access Details'];
            const userAccess = detail?.['User Access Details'];

            const accessDetail =
              Array.isArray(userAccess) && userAccess.length > 0
                ? userAccess[0]
                : userAccess ?? null;

            this.req = {
              englishName: detail?.['DC Name English'] ?? '',
              arabicName: detail?.['DC Name Arabic'] ?? '',
              nationality: detail?.['DC Nationality'] ?? '',
              userType: accessDetail?.['DC User Type'] ?? '',
              access: accessDetail?.['DC User Type'] ?? '',
              legalStatus: detail?.['DC Legal Status'] ?? accessDetail?.['DC Legal Status'] ?? '-',
              issueDate: detail?.['DC Customer Since'] ?? accessDetail?.['DC Customer Since'] ?? '-',
              phone: detail['Work Phone #'] ? detail['Work Phone #'] : detail['Cellular Phone'],
              email: detail?.['Email Address'] ?? ''
            };

            const notifSelection = this.mapNotifResponseToSelection(accessDetail);
            const viewDocumentFlag = accessDetail?.['DC View Document Flag'] ?? detail?.['DC View Document Flag'];
            const adminFlag = accessDetail?.['DC Admin Flag'] ?? detail?.['DC Admin Flag'];

            this.form.patchValue({
              enName: this.req.englishName,
              arName: this.req.arabicName,
              nationality: this.req.nationality,
              userType: this.req.userType,
              email: this.req.email,
              phone: this.req.phone,
              access: this.mapAccessResponseToSelection(accessDetail),
              notif: notifSelection,
              viewDocument: this.mapFlagToYesNo(viewDocumentFlag),
              admin: this.mapFlagToYesNo(adminFlag)

            });
          } else {
            this.sharedService.toastMessage('ERROR', '', 'error', response['Error Message'] || 'Unable to fetch user details');
          }
        },
        error: () => {
          this.loader = false;
        }
      });
  }

  private mapNotifSelectionToPayload(selection: string[]) {
    return {
      allServices: selection.includes('COO_SVS') ? 'Y' : 'N',
      cooServicesOnly: selection.includes('COO') ? 'Y' : 'N',
      svsServicesOnly: selection.includes('SVS') ? 'Y' : 'N',
      transactional: selection.includes('TRANS') ? 'Y' : 'N'
    };
  }

  private mapNotifResponseToSelection(accessDetail: any): string[] {
    const selection: string[] = [];
    if (!accessDetail) {
      return selection;
    }

    const isYes = (val: any) => `${val || ''}`.toUpperCase() === 'Y';
    const readFlag = (keys: string[]) => keys.some(key => isYes(accessDetail[key]));

    if (readFlag(['All Services', 'DC All Service'])) {
      selection.push('COO_SVS');
    }
    if (readFlag(['COO Services Only', 'DC COO Services'])) {
      selection.push('COO');
    }
    if (readFlag(['SVS Services Only', 'DC SVS Services'])) {
      selection.push('SVS');
    }
    if (readFlag(['Transactional', 'DC Transactional'])) {
      selection.push('TRANS');
    }

    return selection;
  }

  private mapAccessResponseToSelection(accessDetail: any): string[] {
    const selection: string[] = [];
    if (!accessDetail) {
      return selection;
    }

    const isYes = (val: any) => `${val || ''}`.toUpperCase() === 'Y';

    if (isYes(accessDetail['DC Membership Services Flag'])) {
      selection.push('MEMBERSHIP');
    }
    if (isYes(accessDetail['DC COO Services Flag'])) {
      selection.push('COO');
    }
    if (isYes(accessDetail['DC SVS Services Flag'])) {
      selection.push('SVS');
    }
    if (isYes(accessDetail['DC Legal Services Flag'])) {
      selection.push('LEGAL');
    }
    if (isYes(accessDetail['DC Information Services Flag'])) {
      selection.push('INFO');
    }
    if (isYes(accessDetail['DC Payment Services Flag'])) {
      selection.push('PAYMENT');
    }
    if (isYes(accessDetail['DC ATA Carnet Flag'])) {
      selection.push('ATA_CARNET');
    }

    return selection;
  }

  private mapAccessSelectionToPayload(selection: string[]) {
    const has = (key: string) => selection.includes(key);
    return {
      "DC Membership Services Flag": has('MEMBERSHIP') ? 'Y' : 'N',
      "DC COO Services Flag": has('COO') ? 'Y' : 'N',
      "DC SVS Services Flag": has('SVS') ? 'Y' : 'N',
      "DC Legal Services Flag": has('LEGAL') ? 'Y' : 'N',
      "DC Information Services Flag": has('INFO') ? 'Y' : 'N',
      "DC Payment Services Flag": has('PAYMENT') ? 'Y' : 'N',
      "DC ATA Carnet Flag": has('ATA_CARNET') ? 'Y' : 'N'
    };
  }
  cancel() {
    let url: any = [`${RoutesPath.USER_DETAIL}`, this.csn, this.userId];
    this.router.navigate(url)
  }

  get viewDocumentOptions() {
    if (this.lang === 'ar') {
      return [
        { label: FieldValue.YES_AR, value: FieldValue.YES_AR },
        { label: FieldValue.NO_AR, value: FieldValue.NO_AR }
      ];
    }

    return [
      { label: FieldValue.YES, value: FieldValue.YES },
      { label: FieldValue.NO, value: FieldValue.NO }
    ];
  }

  private isYesValue(value: any): boolean {
    return value === FieldValue.YES || value === FieldValue.YES_AR || `${value}`.toUpperCase() === 'Y';
  }

  private mapFlagToYesNo(flag: any) {
    const isYes = `${flag || ''}`.toUpperCase() === 'Y';
    return isYes ? (this.lang === 'ar' ? FieldValue.YES_AR : FieldValue.YES) : (this.lang === 'ar' ? FieldValue.NO_AR : FieldValue.NO);
  }
}
